import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { Box, FormControl, Grid, InputAdornment, TextField, Typography, FormControlLabel, Switch } from '@mui/material';
import { LabClient } from 'models/client-directory';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';
import { emptyInvoiceSettingsData } from './form-helper';
import { ClientDirInvoiceSettingsFormData } from 'models/forms';
import { Organization } from 'models/organization';

type InvoiceSettingsFormProps = {
    labClient?: LabClient;
    currentLab: Organization;
    invoiceEnabled: boolean;
    setAllInvoiceSettings?: (settings: any) => void;
    onValidityChange?: (isValid: boolean) => void;
};
const InvoiceSettingsForm: FC<InvoiceSettingsFormProps> = ({
    labClient,
    currentLab,
    setAllInvoiceSettings,
    onValidityChange,
}: InvoiceSettingsFormProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const [isFortnoxEnabled, setIsFortnoxEnabled] = useState<boolean>(false);
    const [isVismaEnabled, setIsVismaEnabled] = useState<boolean>(false);
    const [showPatientPIN, setShowPatientPIN] = useState<boolean>(false);
    const [isDeliveryNoteAttached, setIsDeliveryNoteAttached] = useState<boolean | undefined>(true);
    const [isDeliveryNotesMergedWithInvoice, setIsDeliveryNotesMergedWithInvoice] = useState<boolean>(false);
    const [isMergeSettingForced, setIsMergeSettingForced] = useState<boolean>(false);
    const [forcedMergeValue, setForcedMergeValue] = useState<boolean>(false);
    const currentOrganization = currentOrgVar();

    useEffect(() => {
        if (labClient?.invoiceSettings?.isDeliveryNoteAttached !== null) {
            setIsDeliveryNoteAttached(labClient?.invoiceSettings?.isDeliveryNoteAttached);
        }
        setIsDeliveryNotesMergedWithInvoice(labClient?.invoiceSettings?.isDeliveryNotesMergedWithInvoice ?? false);
        setShowPatientPIN(labClient?.invoiceSettings?.showPatientPIN || false);
        setIsFortnoxEnabled(
            customerSettings.getCustomerSetting(currentOrganization.customerSettings, CONSTANTS.SETTINGS_NAMES.FORTNOX),
        );
        setIsVismaEnabled(
            customerSettings.getCustomerSetting(currentOrganization.customerSettings, CONSTANTS.SETTINGS_NAMES.VISMA),
        );

        const clinicMergeSetting = customerSettings.getCustomerSetting(
            labClient?.customer?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.MERGE_INVOICE_AND_DELIVERY_NOTES,
        );
        const mergeIsForced = clinicMergeSetting === 'ENABLED' || clinicMergeSetting === 'DISABLED';
        setIsMergeSettingForced(mergeIsForced);
        setForcedMergeValue(clinicMergeSetting === 'ENABLED');
    }, [labClient]);

    const validate = (formData: ClientDirInvoiceSettingsFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.paymentTerms) {
            errors.paymentTerms = translate('client-directory:VALIDATION:PAYMENT_TERMS');
        }
        return errors;
    };

    const formik = useFormik<ClientDirInvoiceSettingsFormData>({
        initialValues: emptyInvoiceSettingsData(currentLab, labClient),
        validate,
        // Form is never submitted directly; parent saves via lifted state.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit: () => {},
    });

    useEffect(() => {
        if (!setAllInvoiceSettings) return;
        setAllInvoiceSettings({ settings: formik.values });
    }, [formik.values]);

    useEffect(() => {
        if (!onValidityChange) return;
        onValidityChange(formik.isValid);
    }, [formik.isValid]);

    const handlePaymentTermsChange = (paymentTerms: string) => {
        if (isNaN(parseInt(paymentTerms))) {
            formik.setFieldValue('paymentTerms', null);
            return;
        }
        formik.setFieldValue('paymentTerms', parseInt(paymentTerms));
    };

    const handleInvoiceFeeChange = (invoiceFee: string) => {
        if (isNaN(parseInt(invoiceFee))) {
            formik.setFieldValue('invoiceFee.price', 0);
            return '';
        }
        formik.setFieldValue('invoiceFee.price', parseInt(invoiceFee));
    };

    const handleDeliveryFeeChange = (deliveryFee: string) => {
        if (isNaN(parseInt(deliveryFee))) {
            formik.setFieldValue('deliveryFee.price', 0);
            return '';
        }
        formik.setFieldValue('deliveryFee.price', parseInt(deliveryFee));
    };

    const toggleAttachDeliveryNoteToInvoice = (event: ChangeEvent<HTMLInputElement>) => {
        setIsDeliveryNoteAttached(event.target.checked);
        formik.setFieldValue('isDeliveryNoteAttached', event.target.checked);
    };

    const toggleMergeDeliveryNotesWithInvoice = (event: ChangeEvent<HTMLInputElement>) => {
        setIsDeliveryNotesMergedWithInvoice(event.target.checked);
        formik.setFieldValue('isDeliveryNotesMergedWithInvoice', event.target.checked);
    };

    const togglePatientPinVisibility = (event: ChangeEvent<HTMLInputElement>) => {
        setShowPatientPIN(event.target.checked);
        formik.setFieldValue('showPatientPIN', event.target.checked);
    };

    return (
        <Fragment>
            <Box mt={3}>
                <Box mb={3}>
                    <Typography variant="h4" sx={{ marginBottom: 1 }}>
                        {translate('client-directory:INVOICE_SETTINGS')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        value={formik.getFieldProps('showPatientPIN').value}
                                        color="secondary"
                                        checked={showPatientPIN}
                                        onChange={togglePatientPinVisibility}
                                    />
                                }
                                label={translate('client-directory:DISPLAY_PIN_NUMBER')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        value={formik.getFieldProps('isDeliveryNoteAttached').value}
                                        color="secondary"
                                        checked={isDeliveryNoteAttached}
                                        onChange={toggleAttachDeliveryNoteToInvoice}
                                    />
                                }
                                label={translate('client-directory:ATTACH_DELIVERY_NOTE_WITH_INVOICE')}
                            />
                        </Grid>
                        {isDeliveryNoteAttached && (
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            value={formik.getFieldProps('isDeliveryNotesMergedWithInvoice').value}
                                            color="secondary"
                                            checked={
                                                isMergeSettingForced
                                                    ? forcedMergeValue
                                                    : isDeliveryNotesMergedWithInvoice
                                            }
                                            disabled={isMergeSettingForced}
                                            onChange={toggleMergeDeliveryNotesWithInvoice}
                                        />
                                    }
                                    label={translate('client-directory:MERGE_DELIVERY_NOTES_WITH_INVOICE')}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
                <Box mb={3}>
                    <Typography variant="h4" sx={{ marginBottom: 1 }}>
                        {translate('client-directory:TERMS_AND_FEES')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    required
                                    hasDecimal={false}
                                    label={translate('client-directory:PAYMENT_TERMS')}
                                    helperText={formik.touched.paymentTerms && formik.errors.paymentTerms}
                                    error={formik.touched.paymentTerms && Boolean(formik.errors.paymentTerms)}
                                    onValueChange={handlePaymentTermsChange}
                                    onBlur={formik.handleBlur}
                                    name={formik.getFieldProps('paymentTerms').name}
                                    value={formik.getFieldProps('paymentTerms').value}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {translate('client-directory:DAYS').toLowerCase()}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <MonetaryValueInput
                                    label={translate('client-directory:INVOICE_FEE')}
                                    onValueChange={handleInvoiceFeeChange}
                                    onBlur={formik.handleBlur}
                                    name={formik.getFieldProps('invoiceFee.price').name}
                                    value={formik.getFieldProps('invoiceFee.price').value}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {labClient?.invoiceSettings?.invoiceFee?.currency
                                                    ? translate(
                                                          `client-directory:${labClient.invoiceSettings.invoiceFee.currency.toUpperCase()}`,
                                                      )
                                                    : 'kr'}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <FormControl fullWidth>
                                <MonetaryValueInput
                                    label={translate('client-directory:DELIVERY_FEE')}
                                    onValueChange={handleDeliveryFeeChange}
                                    onBlur={formik.handleBlur}
                                    name={formik.getFieldProps('deliveryFee.price').name}
                                    value={formik.getFieldProps('deliveryFee.price').value}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {labClient?.invoiceSettings?.deliveryFee?.currency
                                                    ? translate(
                                                          `client-directory:${labClient.invoiceSettings.deliveryFee.currency.toUpperCase()}`,
                                                      )
                                                    : 'kr'}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                <Box mb={3}>
                    <Typography variant="h4" sx={{ marginBottom: 1 }}>
                        {translate('client-directory:INVOICE_TEXT')}
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField margin="dense" multiline rows={4} {...formik.getFieldProps('invoiceText')} />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                {isFortnoxEnabled ? (
                    <Box mb={3}>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('client-directory:FORTNOX')}
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        margin="dense"
                                        label={translate('client-directory:FORTNOX_CUSTOMER_NUMBER')}
                                        helperText={formik.touched.fortnoxCustomerNo && formik.errors.fortnoxCustomerNo}
                                        error={
                                            formik.touched.fortnoxCustomerNo && Boolean(formik.errors.fortnoxCustomerNo)
                                        }
                                        {...formik.getFieldProps('fortnoxCustomerNo')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}

                {isVismaEnabled ? (
                    <Box mb={3}>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('client-directory:VISMA')}
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        margin="dense"
                                        fullWidth
                                        label={translate('client-directory:VISMA_CUSTOMER_ID')}
                                        helperText={formik.touched.vismaCustomerId && formik.errors.vismaCustomerId}
                                        error={formik.touched.vismaCustomerId && Boolean(formik.errors.vismaCustomerId)}
                                        {...formik.getFieldProps('vismaCustomerId')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}
            </Box>
        </Fragment>
    );
};

export default InvoiceSettingsForm;
